/**
 * CashOutRequestDialog — iter232
 *
 * Mirror of ContributionRequestDialog for Model 1 symmetry. A member who
 * fronted cash for group expenses (e.g. paid the venue directly) has a
 * positive coin_balance in the group. This dialog lets them request the
 * super admin (Kalpesh) to physically pay them back that credit in cash /
 * GPay. On confirmation, group.fund_balance and member's coin_balance
 * both drop by the amount, and a `cash_out` fund_transactions row is written.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, X, Info } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

const METHODS = [
  { id: 'cash', label: 'Cash' },
  { id: 'gpay', label: 'GPay / UPI' },
  { id: 'imps', label: 'IMPS / NEFT' },
  { id: 'cheque', label: 'Cheque' },
  { id: 'other', label: 'Other' },
];

export default function CashOutRequestDialog({ open, group, maxAmount = 0, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingMine, setPendingMine] = useState([]);

  useEffect(() => {
    if (!open) return;
    setAmount(''); setMethod('cash'); setNote('');
    api.get('/cash-out-requests?status=pending')
      .then((r) => setPendingMine((r.data?.requests || []).filter((x) => x.group_id === group?.id)))
      .catch(() => setPendingMine([]));
  }, [open, group?.id]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    if (maxAmount > 0 && amt > maxAmount) {
      toast.error(`You can cash out at most ₹${Math.floor(maxAmount).toLocaleString('en-IN')}`);
      return;
    }
    setSaving(true);
    try {
      await api.post('/cash-out-requests', {
        group_id: group.id,
        amount: amt,
        method,
        note: note?.trim() || null,
      });
      toast.success('Cash-out request sent to super admin');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send request');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (reqId) => {
    if (!window.confirm('Cancel this pending cash-out request?')) return;
    try {
      await api.post(`/cash-out-requests/${reqId}/cancel`);
      setPendingMine((rows) => rows.filter((r) => r.id !== reqId));
      toast.success('Cancelled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="cash-out-request-dialog">
        <DialogHeader>
          <DialogTitle>Cash Out Credit</DialogTitle>
          <DialogDescription>
            You fronted money for the group. Ask Kalpesh (super admin) to hand you back that credit in cash / GPay.
          </DialogDescription>
        </DialogHeader>

        <div
          className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2.5 flex gap-2 text-xs"
          data-testid="cash-out-info"
        >
          <Info className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
          <p>
            Available credit in <strong>{group?.name}</strong>:{' '}
            <strong className="text-emerald-700">
              ₹{Math.floor(maxAmount || 0).toLocaleString('en-IN')}
            </strong>
            . Once Kalpesh confirms handover, the group fund and your credit both go down by the paid amount.
          </p>
        </div>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Amount (₹)</Label>
            <Input
              type="number"
              min={1}
              max={maxAmount > 0 ? Math.floor(maxAmount) : undefined}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              data-testid="cash-out-amount"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">How will you receive it?</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  className={`p-2 border-2 rounded-lg text-xs font-semibold ${
                    method === m.id
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700'
                      : 'border-border text-muted-foreground'
                  }`}
                  data-testid={`cash-out-method-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="cash-out-note"
              placeholder="e.g. Paid the court on 20 Jan, need cash back"
            />
          </div>
        </div>

        {pendingMine.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Your pending cash-outs</p>
            <div className="space-y-1">
              {pendingMine.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1.5"
                  data-testid={`cash-out-pending-${r.id}`}
                >
                  <span>₹{Number(r.amount).toLocaleString('en-IN')} · {r.method}</span>
                  <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => cancel(r.id)}>
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-3 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !amount}
            data-testid="cash-out-submit"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Send Request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
